"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LazyValue = exports.UrlUtil = exports.DateUtil = exports.StorageUtil = exports.JwtUtil = exports.EventUtil = exports.DomainUtil = exports.DataUtil = exports.OptionsValidator = void 0;
const jwt_decode_1 = require("jwt-decode");
class OptionsValidator {
    constructor(options, defaults, requiredKeys) {
        this.options = this.deepMerge(defaults, options);
        const missingParams = this.validateRequiredParams(this.options, requiredKeys);
        if (missingParams.length > 0) {
            throw new Error(`Missing required parameters: ${missingParams.join(", ")}`);
        }
    }
    deepMerge(defaults, options) {
        const result = Object.assign({}, defaults);
        for (const key in options) {
            if (options[key] &&
                defaults &&
                typeof options[key] === "object" &&
                !Array.isArray(options[key])) {
                result[key] = this.deepMerge(defaults[key], options[key]);
            }
            else {
                result[key] = options[key];
            }
        }
        return result;
    }
    validateRequiredParams(options, requiredKeys, path = []) {
        const missingParams = [];
        for (const key in requiredKeys) {
            const fullPath = [...path, key].join(".");
            const optionValue = options[key];
            const requiredValue = requiredKeys[key];
            if (requiredValue &&
                typeof requiredValue === "object" &&
                !Array.isArray(requiredValue)) {
                if (typeof optionValue !== "object" || optionValue === null) {
                    missingParams.push(fullPath);
                }
                else {
                    missingParams.push(...this.validateRequiredParams(optionValue, requiredValue, [...path, key]));
                }
            }
            else if (requiredValue === true && optionValue === undefined) {
                missingParams.push(fullPath);
            }
        }
        return missingParams;
    }
    getOptions() {
        return this.options;
    }
}
exports.OptionsValidator = OptionsValidator;
class DataUtil {
    static loadScript(url, async = false) {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.async = async;
            script.src = url;
            script.onload = () => resolve(url);
            script.onerror = () => {
                // clean up the failed node
                document.head.removeChild(script);
                reject(new Error(`Failed to load GTM script: ${script.src}`));
            };
            document.head.appendChild(script);
        });
    }
    static loadCSS(href) {
        return new Promise((resolve, reject) => {
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = href;
            link.type = "text/css";
            link.onload = () => resolve(href);
            link.onerror = () => reject(new Error(`Could not load CSS at ${href}`));
            document.head.appendChild(link);
        });
    }
}
exports.DataUtil = DataUtil;
class DomainUtil {
    static getCurrentUrl() {
        const windowLocationHref = window.location.href;
        return new URL(windowLocationHref);
    }
    static getRootDomain(givenHostname = false) {
        const { hostname } = this.getCurrentUrl();
        let hostnameToCheck = hostname;
        if (givenHostname && typeof givenHostname === 'string') {
            hostnameToCheck = givenHostname;
        }
        const parts = hostnameToCheck.split('.');
        return `${parts[parts.length - 2]}.${parts[parts.length - 1]}`;
    }
    static getUrlParamFromScriptTag(fileName, param) {
        const scriptTag = document.querySelector(`script[src*="${fileName}"]`);
        if (!scriptTag) {
            return null;
        }
        const scriptUrl = new URL(scriptTag['src']);
        return scriptUrl.searchParams.get(param);
    }
}
exports.DomainUtil = DomainUtil;
class EventUtil {
    // Generic method to fire a custom event
    static fireEvent(eventName, detail) {
        const event = new CustomEvent(eventName, {
            detail: detail,
            bubbles: true, // Allow the event to bubble up the DOM tree
            cancelable: true, // Allow the event to be cancelled
        });
        document.dispatchEvent(event);
    }
    // Method to listen for a custom event
    static listenForEvent(eventName, callback) {
        const eventListener = (event) => {
            callback(event.detail);
        };
        document.addEventListener(eventName, eventListener);
        // Return a function to remove the listener if needed
        return () => {
            document.removeEventListener(eventName, eventListener);
        };
    }
    /**
     *
     * @param fn This is the function to debounce any events that might be fired back to back or too quickly.
     * @param wait The number of milliseconds to wait before calling the function again.
     * @returns
     */
    static debounce(fn, wait) {
        let lastCalled = 0;
        return function (...args) {
            const now = Date.now();
            if (now - lastCalled >= wait) {
                lastCalled = now;
                fn.apply(this, args);
            }
        };
    }
}
exports.EventUtil = EventUtil;
class JwtUtil {
    static decodeToken(jwt) {
        return (0, jwt_decode_1.jwtDecode)(jwt);
    }
}
exports.JwtUtil = JwtUtil;
class StorageUtil {
    /**
     * Removes a key from localStorage if it exists.
     */
    static deleteStorageObject(objName) {
        try {
            if (typeof window === 'undefined' || !window.localStorage) {
                return;
            }
            if (window.localStorage.getItem(objName) !== null) {
                window.localStorage.removeItem(objName);
            }
        }
        catch (_a) {
            // Swallow: localStorage access can throw in some browsers/contexts.
            return;
        }
    }
}
exports.StorageUtil = StorageUtil;
class DateUtil {
    /**
     * Takes a passed in timestamp and returns true if it is today.
     * @param timestamp The timestamp to check if it is today
     * @returns True if the timestamp is today
     */
    static isTimeStampToday(timestamp) {
        const date = new Date(timestamp);
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
        return date >= startOfDay && date < endOfDay;
    }
    /**
     * Takes a passed in timestamp and returns true if it is expired.
     * @param timestamp The timestamp to check if it is expired
     * @returns True if it is expired.
     */
    static isExpired(timestamp, useSeconds = true) {
        //TODO: Need to handle when timestamp is 10 chars or 13 (depending if milliseconds are inlcuded)
        const now = (useSeconds) ? Math.floor(Date.now() / 1000) : Date.now();
        return timestamp < now;
    }
    static timestampToDate(timestamp) {
        //if we have only 10 digits, we need to multiply by 1000 to get milliseconds
        const fixed = timestamp.toString().length === 10 ? timestamp *= 1000 : timestamp;
        return new Date(fixed).toLocaleString();
    }
}
exports.DateUtil = DateUtil;
class UrlUtil {
    static hasQueryParam(param) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.has(param);
    }
    static hasQueryParamValue(param, value) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param) === value;
    }
    static getQueryParam(param) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }
    static setQueryParam(param, value) {
        const url = new URL(window.location.href);
        url.searchParams.set(param, value);
        window.history.replaceState({}, '', url.toString());
    }
    static removeQueryParam(param) {
        const url = new URL(window.location.href);
        url.searchParams.delete(param);
        window.history.replaceState({}, '', url.toString());
    }
}
exports.UrlUtil = UrlUtil;
class LazyValue {
    get() {
        return this.value;
    }
    ensure(value) {
        if (this.value === undefined) {
            this.value = value;
        }
        return this.value;
    }
}
exports.LazyValue = LazyValue;
